import config from 'config';

const {
  constants: {
    API: { PRICING_UPDATE_URL },
  },
} = config;

/**
 * Streaming version — reads SSE events from the backend and calls
 * the appropriate callback as each event arrives.
 *
 * Anthropic SSE format:
 *   event: <event_type>
 *   data: <json>
 *
 * We parse the raw SSE lines and emit structured callbacks.
 */
export async function streamPropertyAgentChat({
  history,
  context,
  sessionId,
  tools,
  onText,
  onToolUse,
  onDone,
  onError,
}) {
  const resp = await fetch(`${PRICING_UPDATE_URL}/ps/integrations/agents/property/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': window.appAuthToken,
    },
    body: JSON.stringify({
      sessionId,
      context,
      tools,
      messages: history.map((m) => ({
        role: m.role,
        content: m.content,
      })),
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error?.message || `API error ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentToolName = null;
  let currentToolInput = '';
  let currentToolId = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line in buffer

    for (const line of lines) {
      // SseEmitter wraps each line in "data:" prefix
      const stripped = line.startsWith('data:') ? line.slice(5) : line;
      const trimmed = stripped.trim();
      if (!trimmed) continue;

      // The backend forwards raw Anthropic SSE lines, which themselves have "event:" and "data:" prefixes
      // Parse the inner Anthropic SSE format
      if (trimmed.startsWith('event:')) continue; // we use the data line to determine event type

      let dataStr = trimmed;
      if (dataStr.startsWith('data:')) {
        dataStr = dataStr.slice(5).trim();
      }
      if (!dataStr || dataStr === '[DONE]') continue;

      try {
        const parsed = JSON.parse(dataStr);

        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          onText?.(parsed.delta.text);
        }

        if (parsed.type === 'content_block_start' && parsed.content_block?.type === 'tool_use') {
          currentToolName = parsed.content_block.name;
          currentToolId = parsed.content_block.id;
          currentToolInput = '';
        }

        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'input_json_delta') {
          currentToolInput += parsed.delta.partial_json;
        }

        if (parsed.type === 'content_block_stop' && currentToolName) {
          let args = {};
          try { args = JSON.parse(currentToolInput); } catch (e) { /* empty */ }
          onToolUse?.(currentToolName, args);
          currentToolName = null;
          currentToolInput = '';
          currentToolId = null;
        }

        if (parsed.type === 'message_stop') {
          onDone?.();
        }

        if (parsed.type === 'error') {
          onError?.(new Error(parsed.error?.message || 'Stream error'));
        }
      } catch (e) {
        // Not JSON — skip (could be a comment or blank line)
      }
    }
  }

  onDone?.();
}
